import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { canAccess, type Language, type Permission, type PublicUser, canDoAction,
  type AppEntity, type EntityAction,
} from '@mefosa/shared'
import { api, ApiRequestError } from '../api/client'
import { applyDocumentLanguage, cacheLanguage } from '../i18n'

interface AuthValue {
  user: PublicUser | null
  loading: boolean
  signIn: (email: string, password: string) => Promise<void>
  signOut: () => Promise<void>
  setLanguage: (lang: Language) => Promise<void>
  refresh: () => Promise<void>
  /** UI convenience only. The server enforces this independently on every route. */
  allows: (permission: Permission) => boolean
  /**
   * Whether this person kept add / edit / delete on a screen.
   *
   * Also UI convenience: requireAction refuses on the server regardless. It
   * narrows only — it can never grant an action the role does not have.
   */
  allowsAction: (entity: AppEntity, action: EntityAction) => boolean
}

const AuthContext = createContext<AuthValue | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<PublicUser | null>(null)
  const [loading, setLoading] = useState(true)
  const { i18n } = useTranslation()

  const applyLanguage = useCallback(
    (lang: Language) => {
      cacheLanguage(lang)
      applyDocumentLanguage(lang)
      if (i18n.language !== lang) void i18n.changeLanguage(lang)
    },
    [i18n],
  )

  const refresh = useCallback(async () => {
    try {
      const data = await api.get<{ user: PublicUser }>('/auth/me')
      setUser(data.user)
      applyLanguage(data.user.language)
    } catch (err) {
      // 401 simply means nobody is signed in - not an error worth surfacing.
      if (!(err instanceof ApiRequestError) || err.status !== 401) {
        // eslint-disable-next-line no-console
        console.error('Failed to load session', err)
      }
      setUser(null)
    } finally {
      setLoading(false)
    }
  }, [applyLanguage])

  useEffect(() => {
    void refresh()
  }, [refresh])

  const signIn = useCallback(
    async (email: string, password: string) => {
      const data = await api.post<{ user: PublicUser }>('/auth/login', { email, password })
      setUser(data.user)
      applyLanguage(data.user.language)
    },
    [applyLanguage],
  )

  const signOut = useCallback(async () => {
    try {
      await api.post('/auth/logout')
    } finally {
      setUser(null)
    }
  }, [])

  const setLanguage = useCallback(
    async (lang: Language) => {
      applyLanguage(lang)
      setUser((u) => (u ? { ...u, language: lang } : u))
      // Persist per-user so the choice follows them to another device.
      try {
        await api.post('/auth/language', { language: lang })
      } catch {
        // Offline: the local choice still applies for this session.
      }
    },
    [applyLanguage],
  )

  const value = useMemo<AuthValue>(
    () => ({
      user,
      loading,
      signIn,
      signOut,
      setLanguage,
      refresh,
      // The SAME function the server gates on, so the sidebar and the API
      // can never disagree about what somebody may reach.
      allows: (permission) => (user ? canAccess(user, permission) : false),
      allowsAction: (entity, action) => (user ? canDoAction(user, entity, action) : false),
    }),
    [user, loading, signIn, signOut, setLanguage, refresh],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthValue {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>')
  return ctx
}
